import React from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell } from 'utils/groupLabel';
import style from './SeasonEventPerformanceTable.module.css';

const SeasonEventPerformanceTable = ({
  athletes,
  searchText,
  setSearchText,
  selectedAge,
  setSelectedAge,
  selectedGender,
  setSelectedGender,
  availableAges,
}) => {
  // The filter reads Age and "11", or Year and "Y6" for a club that groups by year level, and so does the
  // group beside each athlete's name.
  const { groupingBasis, words } = useGroupLabels();

  const renderStars = (count) => {
    if (!count || count === 0) return null;
    return <span className={style.stars}>{'★'.repeat(count)}</span>;
  };

  return (
    <div className={style.tableCard}>
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className={style.filterGroup}>
          <label className={style.filterLabel}>{words.value}:</label>
          <select
            className={style.selectInput}
            value={selectedAge}
            onChange={(e) => setSelectedAge(e.target.value)}
          >
            <option value="">All</option>
            {availableAges.map((age) => (
              <option key={age} value={age}>
                {formatGroupCell(groupingBasis, age)}
              </option>
            ))}
          </select>
        </div>
        <div className={style.filterGroup}>
          <label className={style.filterLabel}>Gender:</label>
          <select
            className={style.selectInput}
            value={selectedGender}
            onChange={(e) => setSelectedGender(e.target.value)}
          >
            <option value="">All</option>
            <option value="F">F</option>
            <option value="M">M</option>
          </select>
        </div>
      </div>

      {/* What the numbers mean */}
      <p className={style.description}>
        Each athlete is listed with every event they competed in this season. Season Best is their best
        result in the event. Event Season Bests shows a star for each time they beat their previous season
        best in the event, so the first result of the season does not earn one. Events is how many times
        they competed in the event, and Average is the average of those results. Only meets that count for
        season bests are included.
      </p>

      <div className={style.athletesContent}>
        {athletes.length === 0 ? (
          <div className={style.emptyState}>
            No athletes match the current filters
          </div>
        ) : (
          athletes.map((athlete) => (
            <div key={athlete.athleteID} className={style.athleteSection}>
              <h2 className={style.athleteName}>
                {athlete.firstName} {athlete.lastName} {formatGroupCell(groupingBasis, athlete.age)} {athlete.gender}
              </h2>
              <table className={style.eventTable}>
                <thead>
                  <tr>
                    <th className={style.colEvent}>Event</th>
                    <th className={style.colSeasonPB}>Season Best</th>
                    <th className={style.colSeasonPBs}>Event Season Bests</th>
                    <th className={style.colEvents}>Events</th>
                    <th className={style.colAverage}>Average</th>
                  </tr>
                </thead>
                <tbody>
                  {athlete.events.map((event) => (
                    <tr key={event.eventTypeCode}>
                      <td>{event.eventName}</td>
                      <td>{event.seasonPB}</td>
                      <td>{renderStars(event.eventSeasonPBs)}</td>
                      <td>{event.eventCount}</td>
                      <td>{event.averageResult}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

SeasonEventPerformanceTable.propTypes = {
  athletes: PropTypes.array,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
  selectedAge: PropTypes.string,
  setSelectedAge: PropTypes.func,
  selectedGender: PropTypes.string,
  setSelectedGender: PropTypes.func,
  availableAges: PropTypes.array,
};

export default SeasonEventPerformanceTable;
